import {type} from 'arktype'
import Big from 'big.js'
import {dequal} from 'dequal'

import {isReadableError} from '@/lib/api/ReadableError'
import {responseData} from '@/lib/api/ResponseData'
import throwIfError from '@/lib/api/throwIfError'
import cartDb from '@/lib/cart/cartDb'
import {getCartQueryKey} from '@/lib/cart/hooks/useCart'
import {getCurrency} from '@/lib/locale/country'
import {
  getLanguageCountryFromLocale,
  localeUnderscoreToHyphen,
  type SupportedLocales,
} from '@/lib/locale/locale'
import {type CartItem, transformRawCartItem} from '@/models/cartModel'
import {type Order, transformOrder} from '@/models/orderModel'
import {CartCalibratedError, fixCartItems, isCartCalibratedError} from '@/services/cartService'
import call from '@/utils/api/call'
import shouldLogError from '@/utils/api/shouldLogError'
import UnexpectedError from '@/utils/api/UnexpectedError'
import ParseError, {isParseError} from '@/utils/errors/ParseError'
import PermanentError from '@/utils/errors/PermanentError'
import TransformError from '@/utils/errors/TransformError'
import UserFriendlyError from '@/utils/errors/UserFriendlyError'
import {logError} from '@/utils/logger'

import {
  type Addresses,
  AddressesResponse,
  type MinimalCartItem,
  paymentTypeCheck,
  PromotionCodeResponse,
  type ShippingInfo,
  type StripeAddress,
  TaxResponse,
  WalletResponse,
} from './paymenModel'

const NON_ERROR_API_CODES: string[] = ['ICA0000']

export async function getCustomerSession({signal}: {signal?: AbortSignal}) {
  return call
    .post(
      `/api/checkout/v1/payments/customer-sessions`,
      {},
      {
        signal,
      },
    )
    .then(res => {
      const msg = throwIfError(res, NON_ERROR_API_CODES, true)
      if (msg) {
        throw new PermanentError(msg)
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- safe
      const data = res.data.data as object | undefined // type-coverage:ignore-line
      if (
        data &&
        'customerSessionClientSecret' in data &&
        typeof data.customerSessionClientSecret === 'string'
      ) {
        return data.customerSessionClientSecret
      }
      throw new PermanentError('Cannot get customer session')
    })
    .catch(e => {
      throw e
    })
}

const CashbackResponse = responseData({
  value: type('string.numeric').pipe.try(value => Big(value)),
  rate: type('string.numeric').pipe.try(value => Big(value)),
})

export async function getCashback({signal, amount}: {signal?: AbortSignal; amount: number}) {
  return call
    .post(
      `/api/checkout/v1/cashbacks/compute`,
      {
        amount,
      },
      {
        signal,
      },
    )
    .then(res => {
      throwIfError(res, NON_ERROR_API_CODES)

      const cashbackResponse = CashbackResponse(res.data) // type-coverage:ignore-line
      return cashbackResponse.data
    })
    .catch(e => {
      if (shouldLogError(e)) {
        logError(e)
      }

      if (isReadableError(e)) {
        throw new UserFriendlyError(e.message, e)
      }

      throw new UnexpectedError(e)
    })
}

// There are 3 cases:
// 1. Buy now for users: items are provided
// 2. Buy cart for users: items are not provided
// 3. For guests: if items are provided, use them, otherwise use the local cart
export async function createPaymentIntent({
  isAuthenticated,
  confirmationTokenId,
  returnUrl,
  signal,
  applyRewards,
  coupon,
  queryClient,
  locale,
  items,
  setCustomItems,
  email,
  addresses,
  newAddress,
  roundUp,
}: (
  | {
      items?: never
      setCustomItems?: never
    }
  | {
      items?: CartItem[]
      setCustomItems: (items: CartItem[]) => void
    }
) & {
  locale: SupportedLocales
  signal?: AbortSignal
  confirmationTokenId: string
  isAuthenticated: boolean
  applyRewards: boolean
  roundUp: boolean
  returnUrl: string
  queryClient: QueryClient
  coupon?: string
  email: string
  addresses?: Addresses
  newAddress?: ShippingInfo
}): Promise<{clientSecret: string; order: Order; paymentIntentStatus: string}> {
  const willUseGuestCart = !isAuthenticated

  const {country} = getLanguageCountryFromLocale(locale)
  const currency = getCurrency(country)?.toLowerCase()

  if (!currency) {
    throw new UserFriendlyError('Unsupported country', new Error()).permanent()
  }

  const api = (() => {
    if (isAuthenticated) {
      if (items) {
        // Buy now for users
        return '/api/checkout/v1/payments/payment-intents-for-line-items'
      }
      // Buy cart for users
      return '/api/checkout/v1/payments/payment-intents'
    }

    // For guests
    return '/api/checkout/pub/v1/payments/payment-intents'
  })()

  const data = willUseGuestCart
    ? await (async () => {
        const {v4: uuidV4} = await import('uuid')

        const uuid = uuidV4()
        return {
          cartId: uuid,
          checkoutType: 'STRIPE',
          lineItems: items
            ? items.map(item => transformRawCartItem(item, currency))
            : cartDb.getCart(country).items.map(item => transformRawCartItem(item, currency)),
          confirmationTokenId,
          useWallet: applyRewards,
          roundUp,
          code: coupon,
          returnUrl: globalThis.location.origin + returnUrl,
          email,
        }
      })()
    : {
        checkoutType: 'STRIPE',
        confirmationTokenId,
        lineItems: items ? items.map(item => transformRawCartItem(item, currency)) : undefined,
        useWallet: applyRewards,
        roundUp,
        code: coupon,
        returnUrl: globalThis.location.origin + returnUrl,
      }

  return call
    .post(api, data, {
      signal,
      headers: {
        'Accept-Language': localeUnderscoreToHyphen(locale),
      },
    })
    .then(res => {
      const data = res.data as object // type-coverage:ignore-line
      if (
        'code' in data &&
        typeof data.code === 'string' &&
        ['ICA1120', 'ICA1134'].includes(data.code)
      ) {
        throw new CartCalibratedError(data)
      }

      throwIfError(res, NON_ERROR_API_CODES)

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- safe
      const dataData = res.data.data as unknown // type-coverage:ignore-line

      if (!paymentTypeCheck.Check(dataData)) {
        throw new ParseError(
          'Payment',
          'Invalid intent',
          Array.from(paymentTypeCheck.Errors(dataData)),
        )
      }

      const order = transformOrder(dataData.order)

      if (!order) {
        throw new TransformError('Payment', 'Invalid order', dataData.order)
      }

      if (newAddress) {
        const shippingInfos = addresses?.shippingInfos?.slice() ?? []

        /* eslint-disable @typescript-eslint/prefer-nullish-coalescing -- our backend is stupid */
        const newShippingInfo: ShippingInfo = {
          city: newAddress.city || undefined,
          state: newAddress.state || undefined,
          country: newAddress.country || undefined,
          line1: newAddress.line1 || undefined,
          line2: newAddress.line2 || undefined,
          name: newAddress.name || undefined,
          postalCode: newAddress.postalCode || undefined,
          phone: newAddress.phone || undefined,
        }
        /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */

        const cleanedNewShippingInfo = JSON.parse(JSON.stringify(newShippingInfo))

        const shouldAddNewAddress = shippingInfos.every(
          shippingInfo => !dequal(shippingInfo, cleanedNewShippingInfo),
        )

        if (shouldAddNewAddress) {
          shippingInfos.push(newShippingInfo)

          void putAddresses({
            addresses: {
              shippingInfos,
            },
          })
        }
      }

      return {
        clientSecret: dataData.clientSecret,
        order,
        paymentIntentStatus: dataData.paymentIntentStatus,
      }
    })
    .catch(e => {
      if (shouldLogError(e)) {
        console.error('Cannot checkout cart', e)
      }
      if (isCartCalibratedError(e)) {
        if (items) {
          const fixedItems = fixCartItems(items, e)
          setCustomItems(fixedItems)
        } else {
          if (willUseGuestCart) {
            // PATCH cartDb
            const currentCart = cartDb.getCart(country)
            currentCart.items = fixCartItems(currentCart.items, e)
            cartDb.updateCart(currentCart, true)
          }
          // TODO: instead of refetching cart, we should show an advanced UI to the user and let them know about the old price, new price
          void queryClient.refetchQueries({queryKey: getCartQueryKey({locale})})
        }
        throw e
      }
      if (isReadableError(e)) {
        throw new UserFriendlyError(e.res.data.msg as string, e) // type-coverage:ignore-line
      }
      throw new UnexpectedError(e)
    })
}

export async function getTax({
  signal,
  lineItems,
  address,
  locale,
}: {
  signal?: AbortSignal
  lineItems: MinimalCartItem[]
  address: StripeAddress
  locale: SupportedLocales
}) {
  const currency = getCurrency(address.country)?.toLowerCase()

  invariant(currency, 'Cannot get currency')

  const realLineItems = lineItems.reduce<MinimalCartItem>(
    (acc, item) => {
      return {
        ...acc,
        price: acc.price + item.price * item.quantity,
      }
    },
    {
      id: 'id',
      quantity: 1,
      price: 0,
    },
  )

  if (realLineItems.price <= 0) {
    return {
      taxAmount: 0,
    }
  }

  return call
    .post(
      '/api/checkout/pub/v1/taxes',
      {
        lineItems: [realLineItems],
        currency,
        shippingInfo: address,
      },
      {
        signal,
        headers: {
          'Accept-Language': localeUnderscoreToHyphen(locale),
        },
      },
    )
    .then(res => {
      const msg = throwIfError(res, NON_ERROR_API_CODES, true)
      if (msg) {
        throw new Error(msg)
      }

      const resData = TaxResponse(res.data) // type-coverage:ignore-line

      if (resData instanceof type.errors) {
        // hover out.summary to see validation errors
        console.error(resData.summary)
        throw new ParseError('Tax', resData.summary, resData.issues)
      }

      return {
        taxAmount: resData.data.taxAmount,
      }
    })
}

export async function getAddresses({signal}: {signal?: AbortSignal}) {
  return call
    .get('/api/checkout/v1/addresses', {
      signal,
    })
    .then(res => {
      const msg = throwIfError(res, NON_ERROR_API_CODES, true)
      if (msg) {
        throw new Error(msg)
      }

      const resData = AddressesResponse(res.data) // type-coverage:ignore-line

      if (resData instanceof type.errors) {
        throw new ParseError('Customer', resData.summary, resData.issues)
      }

      return resData.data
    })
}

export async function putAddresses({
  signal,
  addresses,
}: {
  signal?: AbortSignal
  addresses: Addresses
}) {
  return call.put('/api/checkout/v1/addresses', addresses, {
    signal,
  })
}

export async function getPromotionCodeInfo(code: string) {
  return call
    .get('/api/checkout/pub/v1/promotion-codes', {
      params: {
        code,
      },
    })
    .then(res => {
      throwIfError(res, NON_ERROR_API_CODES)

      const resData = PromotionCodeResponse(res.data) // type-coverage:ignore-line

      if (resData instanceof type.errors) {
        throw new ParseError('Promotion code', resData.summary, resData.issues)
      }

      return resData.data
    })
    .catch(e => {
      if (isParseError(e)) {
        throw new UserFriendlyError('Promotion code is invalid', e).permanent()
      }

      if (isReadableError(e)) {
        if (e.res.data.code === 'ICA1100') {
          throw new UserFriendlyError('Promotion code is invalid', e).permanent()
        }

        if (e.res.data.code === 'ICA1138') {
          throw new UserFriendlyError(
            'Promotion code has reached the maximum number of redemptions',
            e,
          ).permanent()
        }

        if (e.res.data.code === 'ICA1139') {
          throw new UserFriendlyError('Promotion code is inactive', e).permanent()
        }

        if (e.res.data.code === 'ICA1140') {
          throw new UserFriendlyError('Promotion code is expired', e).permanent()
        }
      }

      throw new UserFriendlyError('Promotion code is invalid', e)
    })
}

export async function getWalletBalance({signal}: {signal?: AbortSignal}) {
  return call
    .get('/api/checkout/v1/wallets', {
      signal,
    })
    .then(res => {
      throwIfError(res, NON_ERROR_API_CODES)
      const {data} = WalletResponse(res.data)
      return data
    })
}

export async function setWalletRoundUp({
  roundUp,
  signal,
}: {
  roundUp: boolean
  signal?: AbortSignal
}) {
  return call
    .patch(
      '/api/checkout/v1/wallets/round-up',
      {
        roundUp,
      },
      {
        signal,
      },
    )
    .then(res => {
      throwIfError(res, NON_ERROR_API_CODES)
      const {data} = WalletResponse(res.data)
      return data
    })
}
